import { useEffect } from "react";
import { Helmet } from "react-helmet-async";
import { Link } from "react-router-dom";
import { Header } from "@/components/Header";
import { Breadcrumb } from "@/components/Breadcrumb";
import { Footer } from "@/components/Footer";
import { Button } from "@/components/ui/button";
import { MapPin } from "lucide-react";

const serviceAreas = {
  "Passaic County": [
    { name: "Wayne", slug: "wayne" },
    { name: "Totowa", slug: "totowa" },
    { name: "Little Falls", slug: "little-falls" },
    { name: "Woodland Park", slug: "woodland-park" },
    { name: "Clifton", slug: "clifton" },
    { name: "Passaic", slug: "passaic" },
    { name: "Pompton Lakes", slug: "pompton-lakes" },
  ],
  "Bergen County": [
    { name: "Hackensack", slug: "hackensack" },
    { name: "Paramus", slug: "paramus" },
    { name: "Fair Lawn", slug: "fair-lawn" },
    { name: "Ridgewood", slug: "ridgewood" },
    { name: "Wyckoff", slug: "wyckoff" },
    { name: "Glen Rock", slug: "glen-rock" },
  ],
  "Essex County": [
    { name: "Montclair", slug: "montclair" },
    { name: "Livingston", slug: "livingston" },
    { name: "West Orange", slug: "west-orange" },
    { name: "Nutley", slug: "nutley" },
    { name: "Bloomfield", slug: "bloomfield" },
    { name: "Caldwell", slug: "caldwell" },
  ],
  "Morris County": [
    { name: "Morristown", slug: "morristown" },
    { name: "Parsippany", slug: "parsippany" },
    { name: "Denville", slug: "denville" },
    { name: "Boonton", slug: "boonton" },
    { name: "Mountain Lakes", slug: "mountain-lakes" },
    { name: "Morris Plains", slug: "morris-plains" },
  ],
};

const cityPages: Record<string, string> = {
  "passaic-county/clifton": "/service-areas/passaic-county/clifton",
  "passaic-county/passaic": "/service-areas/passaic-county/passaic",
};

function getCityLink(countySlug: string, citySlug: string): string | null {
  return cityPages[`${countySlug}/${citySlug}`] || null;
}

export default function ServiceAreas() {
  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <>
      <Helmet>
        <title>Masonry & Concrete Service Areas North Jersey | Masonry Pro</title>
        <meta property="og:description" content="Masonry Pro serves Passaic, Bergen, Essex, and Morris counties in North Jersey. Patios, retaining walls, concrete work. 35+ years experience. Free estimates." />
        <meta property="og:title" content="Masonry & Concrete Service Areas North Jersey | Masonry Pro" />
        <meta 
          name="description" 
          content="Masonry Pro serves Passaic, Bergen, Essex, and Morris counties in North Jersey. Patios, retaining walls, concrete work. 35+ years experience. Free estimates." 
        />
        <meta property="og:image" content="https://masonry-pro.com/og/services.jpg" />
        <meta property="og:image:width" content="1200" />
        <meta property="og:image:height" content="630" />
        <meta property="og:image:type" content="image/jpeg" />
      </Helmet>

      <div className="min-h-screen bg-background">
        <Header />
        <Breadcrumb />
        
        <main>
          {/* Hero Section */}
          <section className="relative pb-20 bg-gradient-to-b from-primary/20 to-background">
            <div className="container mx-auto px-4">
              <div className="max-w-4xl mx-auto text-center">
                <div className="flex items-center justify-center gap-2 mb-4">
                  <MapPin className="h-6 w-6 text-secondary" />
                  <span className="text-secondary font-medium">North New Jersey</span>
                </div>
                <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold mb-6">
                  Our <span className="text-gradient-gold">Service Areas</span>
                </h1>
                <p className="text-xl text-muted-foreground max-w-2xl mx-auto">
                  Based in Totowa, NJ, we proudly serve homeowners across Passaic, Bergen, Essex, and Morris counties with expert masonry and concrete services.
                </p>
              </div>
            </div>
          </section>

          {/* Counties Grid */}
          <section className="py-20">
            <div className="container mx-auto px-4">
              <div className="grid md:grid-cols-2 gap-8 max-w-5xl mx-auto">
                {Object.entries(serviceAreas).map(([county, cities]) => {
                  const slug = county.toLowerCase().replace(/\s+/g, "-");
                  return (
                  <div key={county} className="bg-card rounded-xl p-6 border border-border">
                    <Link to={`/service-areas/${slug}`} className="block">
                      <h2 className="text-2xl font-bold mb-4 text-gradient-gold hover:opacity-80 transition-opacity">{county}</h2>
                    </Link>
                    <div className="grid grid-cols-2 gap-2">
                      {cities.map((city) => {
                        const cityLink = getCityLink(slug, city.slug);
                        return cityLink ? (
                          <Link
                            key={city.slug}
                            to={cityLink}
                            className="flex items-center gap-2 py-2 text-muted-foreground hover:text-secondary transition-colors"
                          >
                            <MapPin className="h-4 w-4 text-secondary/60" />
                            {city.name}
                          </Link>
                        ) : (
                          <div
                            key={city.slug}
                            className="flex items-center gap-2 py-2 text-muted-foreground"
                          >
                            <MapPin className="h-4 w-4 text-secondary/60" />
                            {city.name}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                  );
                })}
              </div>
            </div>
          </section>

          {/* Why Local Matters */}
          <section className="py-20 bg-card">
            <div className="container mx-auto px-4">
              <div className="max-w-3xl mx-auto text-center">
                <h2 className="text-3xl md:text-4xl font-bold mb-6">
                  Why <span className="text-gradient-gold">Local</span> Matters
                </h2>
                <p className="text-lg text-muted-foreground mb-8">
                  We've worked in North Jersey for over 35 years. That means we understand local soil conditions, drainage challenges, and building codes. We're not a faceless company from out of state. We're your neighbors.
                </p>
                <div className="grid md:grid-cols-3 gap-6 text-center">
                  <div>
                    <div className="text-4xl font-bold text-secondary mb-2">35+</div>
                    <p className="text-muted-foreground">Years in Business</p>
                  </div>
                  <div>
                    <div className="text-4xl font-bold text-secondary mb-2">4</div>
                    <p className="text-muted-foreground">Counties Served</p>
                  </div>
                  <div>
                    <div className="text-4xl font-bold text-secondary mb-2">1000+</div>
                    <p className="text-muted-foreground">Projects Completed</p>
                  </div>
                </div>
              </div>
            </div>
          </section>

          {/* CTA Section */}
          <section className="py-20 bg-gradient-to-r from-primary to-accent">
            <div className="container mx-auto px-4 text-center">
              <h2 className="text-3xl md:text-4xl font-bold mb-4 text-primary-foreground">
                Ready to Start Your Project?
              </h2>
              <p className="text-xl text-primary-foreground/80 mb-8 max-w-2xl mx-auto">
                Get a free estimate from your local masonry experts.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <Button variant="gold" size="lg">
                  Schedule Your Free Estimate
                </Button>
                <Button 
                  variant="outline" 
                  size="lg"
                  className="border-primary-foreground text-primary-foreground hover:bg-primary-foreground/10"
                  asChild
                >
                  <a href="tel:+19737252395">(973) 725-2395</a>
                </Button>
              </div>
            </div>
          </section>
        </main>

        <Footer />
      </div>
    </>
  );
}
